import { Course } from '../types';

export const getCourseColor = (course: Course): string | undefined => {
  const { facultyCode, category1, category2 } = course;

  // 1. faculty が "pg" -> #808080 (グレー：大学院)
  if (facultyCode === 'pg') {
    return '#808080';
  }

  // 2. faculty が "js" -> #15AABF (シアン：国際日本)
  if (facultyCode === 'js') {
    return '#15AABF';
  }

  // 3. category1 が "lnc" または category2 が いずれかの値を持つ -> #E54D42 (レッド：言語系)
  const redCategory2Codes = ['noml', 'ml', 'gl', 'fl', 'rl', 'fj', 'sj'];
  if (category1 === 'lnc' || (category2 && redCategory2Codes.includes(category2))) {
    return '#E54D42';
  }

  // 4. facultyCode が "gla" かつ category1 が特定のコード(fc, lac, arc, tc) -> #40C057 (グリーン：世界教養)
  const greenCategory1Codes = ['fc', 'lac', 'arc', 'tc'];
  if (facultyCode === 'gla' && category1 && greenCategory1Codes.includes(category1)) {
    return '#40C057';
  }

  if (facultyCode === 'lc') {
    if (category1 === 'ic') return '#339AF0'; // 明るい青 (導入)
    if (category1 === 'sc') return '#005BB5'; // 濃い青 (概論)
    if (category1 === 'mc') return '#003366'; // 紺 (専門)
  }

  // 6. faculty が "ia" (国際社会学部) かつ category1 が一致
  if (facultyCode === 'ia') {
    if (category1 === 'ic') return '#FF4D94'; // 鮮やかなピンク (導入)
    if (category1 === 'sc') return '#D6336C'; // ローズ (概論)
    if (category1 === 'mc') return '#862E9C'; // 紫 (専門)
  }

  // 上記のいずれにも当てはまらない場合は「指定なし」
  return '#808080';
};

// 以前使用していた色のマッピング（移行用）
const COLOR_MIGRATION_MAP: { [key: string]: string } = {
  '#AAAAAA': '#808080', // pg
  '#4A90E2': '#15AABF', // js (migrating from older blue to new cyan)
  '#2D80E4': '#15AABF', // js (migrating previous blue to new cyan)
  '#F25B4D': '#E54D42', // redCategory2
  '#87CEEB': '#339AF0', // lc ic
  '#0000FF': '#005BB5', // lc sc (migrating from older blue to new darker blue)
  '#1971C2': '#005BB5', // migrating previous dark blue to new darker blue
  '#000080': '#003366', // lc mc
  '#FFC0CB': '#FF4D94', // ia ic
  '#FF00FF': '#D6336C', // ia sc
  '#800080': '#862E9C', // ia mc
  '#A0A0A0': '#808080', // default gray
};

/**
 * 授業の表示色を決定する。
 * 保存されている色が古いデフォルト値の場合は、新しいデフォルト値に移行する。
 */
export const getDisplayColor = (course: Course): string | undefined => {
  const savedColor = course.color;

  // 1. 保存された色が古いデフォルト値のリストにある場合、新しい値を返す
  if (savedColor && COLOR_MIGRATION_MAP[savedColor.toUpperCase()]) {
    return COLOR_MIGRATION_MAP[savedColor.toUpperCase()];
  }

  // 2. ユーザーが明示的に色を選択して保存している場合は、それを最優先する
  // (COLOR_MIGRATION_MAPに引っかからなかったsavedColorは、ユーザーが選んだカスタム色か、新しいデフォルト色)
  if (savedColor) {
    return savedColor;
  }

  // 3. getCourseColor で計算される色が定義されていれば、それを返す（未設定の場合）
  const computedColor = getCourseColor(course);
  if (computedColor) {
    return computedColor;
  }

  return '#808080';
};
